import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './EducationPopup.css';

const EducationPopup = ({ isOpen, onClose, onSave, editingEducation }) => {
  const [formData, setFormData] = useState({
    degree: '',
    institution: '',
    field_of_study: '',
    start_date: '',
    end_date: '',
    is_current: false,
    location: '',
    achievements: [],
    institution_logo: null
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [newAchievement, setNewAchievement] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (editingEducation) {
        // Populate form with existing education data
        const isCurrent = editingEducation.is_current || false;
        setFormData({
          degree: editingEducation.degree || '',
          institution: editingEducation.institution || '',
          field_of_study: editingEducation.field_of_study || '',
          start_date: editingEducation.start_date || '',
          // Clear end_date if currently studying
          end_date: isCurrent ? '' : (editingEducation.end_date || ''),
          is_current: isCurrent,
          location: editingEducation.location || '',
          achievements: editingEducation.achievements || [],
          institution_logo: editingEducation.institution_logo || null
        });
      } else {
        // Reset form for new education
        setFormData({
          degree: '',
          institution: '',
          field_of_study: '',
          start_date: '',
          end_date: '',
          is_current: false,
          location: '',
          achievements: [],
          institution_logo: null
        });
      }
      setErrors({});
      setNewAchievement('');
    }
  }, [isOpen, editingEducation]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If "Currently studying" checkbox is checked, clear end_date
    if (name === 'is_current' && checked) {
      setFormData(prev => ({
        ...prev,
        is_current: true,
        end_date: '' // Clear end_date when currently studying is checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Clear end_date error when is_current is checked
    if (name === 'is_current' && checked && errors.end_date) {
      setErrors(prev => ({
        ...prev,
        end_date: null
      }));
    }
  };

  const handleAddAchievement = () => {
    if (newAchievement.trim()) {
      setFormData(prev => ({
        ...prev,
        achievements: [...prev.achievements, newAchievement.trim()]
      }));
      setNewAchievement('');
    }
  };

  const handleRemoveAchievement = (index) => {
    setFormData(prev => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const submitData = {
        ...formData,
        // Convert achievements array to string if needed by backend
        achievements: formData.achievements
      };

      // If currently studying, don't send end_date (set to null)
      if (submitData.is_current) {
        submitData.end_date = null;
      }

      let response;
      if (editingEducation) {
        response = await ApiService.put(`/education/${editingEducation.id}`, submitData);
      } else {
        response = await ApiService.post('/education', submitData);
      }

      if (response.success) {
        onSave(response.data);
        onClose();
      } else {
        setErrors(response.errors || {});
      }
    } catch (error) {
      console.error('Error saving education:', error);
      setErrors({ general: 'Failed to save education. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="education-popup-overlay">
      <div className="education-popup">
        <div className="education-popup-header">
          <h3>{editingEducation ? 'Edit Education' : 'Add Education'}</h3>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="education-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="degree">Degree *</label>
              <input
                type="text"
                id="degree"
                name="degree"
                value={formData.degree}
                onChange={handleInputChange}
                placeholder="e.g., Bachelor of Science, Master of Arts"
                className={errors.degree ? 'error' : ''}
                required
              />
              {errors.degree && <span className="error-text">{errors.degree[0]}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="institution">Institution *</label>
              <input
                type="text"
                id="institution"
                name="institution"
                value={formData.institution}
                onChange={handleInputChange}
                placeholder="e.g., University of California"
                className={errors.institution ? 'error' : ''}
                required
              />
              {errors.institution && <span className="error-text">{errors.institution[0]}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="field_of_study">Field of Study *</label>
              <input
                type="text"
                id="field_of_study"
                name="field_of_study"
                value={formData.field_of_study}
                onChange={handleInputChange}
                placeholder="e.g., Computer Science, Business Administration"
                className={errors.field_of_study ? 'error' : ''}
                required
              />
              {errors.field_of_study && <span className="error-text">{errors.field_of_study[0]}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., Los Angeles, CA"
                className={errors.location ? 'error' : ''}
              />
              {errors.location && <span className="error-text">{errors.location[0]}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_date">Start Date *</label>
              <input
                type="month"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className={errors.start_date ? 'error' : ''}
                required
              />
              {errors.start_date && <span className="error-text">{errors.start_date[0]}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date</label>
              <input
                type="month"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                disabled={formData.is_current}
                className={errors.end_date ? 'error' : ''}
              />
              {errors.end_date && <span className="error-text">{errors.end_date[0]}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group education-checkbox-group">
              <label className="education-checkbox-label">
                <input
                  type="checkbox"
                  name="is_current"
                  checked={formData.is_current}
                  onChange={handleInputChange}
                  className="education-checkbox-input"
                />
                <span className="education-checkbox-text">Currently studying</span>
              </label>
            </div>
            <div className="form-group">
              {/* Empty div to maintain grid layout */}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="institution_logo">Institution Logo URL</label>
            <input
              type="url"
              id="institution_logo"
              name="institution_logo"
              value={formData.institution_logo || ''}
              onChange={handleInputChange}
              placeholder="https://example.com/logo.png"
              className={errors.institution_logo ? 'error' : ''}
            />
            {errors.institution_logo && <span className="error-text">{errors.institution_logo[0]}</span>}
          </div>


          <div className="form-group">
            <label>Achievements</label>
            <div className="achievements-input">
              <input
                type="text"
                value={newAchievement}
                onChange={(e) => setNewAchievement(e.target.value)}
                placeholder="Add an achievement..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAchievement())}
              />
              <button type="button" onClick={handleAddAchievement} className="add-achievement-btn">
                Add
              </button>
            </div>
            
            {formData.achievements.length > 0 && (
              <div className="achievements-list">
                {formData.achievements.map((achievement, index) => (
                  <div key={index} className="achievement-item">
                    <span>{achievement}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAchievement(index)}
                      className="remove-achievement-btn"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {errors.general && (
            <div className="error-message">
              {errors.general}
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (editingEducation ? 'Update Education' : 'Add Education')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EducationPopup;
